import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { SelectItem } from 'primeng/api';
import { selectPlants } from '../../../../../store/selectors/plant.selector';
import { Plant, PlannerGroup } from '../../../../../models/plant.model';
import { FunctionalLocation } from '../../../../../models/functional-location.model';
import { FunctionalLocationService } from '../../../../../services/functional-location.service';
import * as plantActions from '../../../../../store/actions/plant.actions';
import { Rule } from '../../../../../models/rule.model';
import { WorkCenterLabel } from '../../../../../models/work-center-label.model';
import { WorkCenterLabelService } from '../../../../../services/work-center-label.service';

@Component({
  selector: 'app-localisation-step',
  templateUrl: './localisation-step.component.html',
  standalone: false,
})
export class LocalisationStepComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;
  @Input() rule?: Rule;
  @Input() mode: 'create' | 'edit' = 'edit';
  @Input() plantLocked = false;
  @Input() selectedLabelIds: string[] = [];
  @Output() selectedLabelIdsChange = new EventEmitter<string[]>();
  @Input() initialLabelIds: string[] = [];

  private destroy$ = new Subject<void>();

  plantOptions$ = new Observable<SelectItem[]>();
  plannerGroupOptions$ = new Observable<SelectItem[]>();
  workCenterOptions$ = new Observable<SelectItem[]>();
  functionalLocationOptions: SelectItem[] = [];
  loadingFunctionalLocations = false;

  plants: Plant[] = [];

  // Sub-sector labels
  availableLabels: WorkCenterLabel[] = [];
  loadingLabels = false;

  constructor(
    private store: Store,
    private functionalLocationService: FunctionalLocationService,
    private workCenterLabelService: WorkCenterLabelService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(plantActions.loadPlants());
    this.setupStoreSubscriptions();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasErrors(): boolean {
    const fields = [
      'plant',
      'plannerGroup',
      'workCenter',
      'functionalLocation',
    ];
    return fields.some((f) => {
      const ctrl = this.formGroup.get(f);
      return ctrl && ctrl.invalid && ctrl.touched;
    });
  }

  private setupStoreSubscriptions(): void {
    this.plantOptions$ = this.store.select(selectPlants).pipe(
      map((plants: Plant[]) => {
        this.plants = plants;
        return plants.map((plant) => ({
          label: plant.sapDescription || plant.sapId,
          value: plant.sapId,
        }));
      }),
    );

    this.plannerGroupOptions$ = this.formGroup.get('plant')!.valueChanges.pipe(
      startWith(this.formGroup.get('plant')!.value || ''),
      map((selectedPlantId: string) => {
        const plant = this.plants.find((p) => p.sapId === selectedPlantId);
        return (
          plant?.plannerGroups
            ?.map((pg) => ({
              label: pg.sapDescription || pg.sapId,
              value: pg.sapId,
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || []
        );
      }),
    );

    this.workCenterOptions$ = this.formGroup
      .get('plannerGroup')!
      .valueChanges.pipe(
        startWith(this.formGroup.get('plannerGroup')!.value || ''),
        map((selectedPlannerGroupId: string) => {
          const plant = this.plants.find((p) =>
            p.plannerGroups?.some((pg) => pg.sapId === selectedPlannerGroupId),
          );
          const plannerGroup = plant?.plannerGroups?.find(
            (pg) => pg.sapId === selectedPlannerGroupId,
          );
          return (
            plannerGroup?.workCenters
              ?.map((wc) => ({
                label: wc.sapDescription || wc.sapId,
                value: wc.sapId,
              }))
              ?.sort((a, b) => a.label.localeCompare(b.label)) || []
          );
        }),
      );
  }

  private setupFormSubscriptions(): void {
    this.formGroup
      .get('plant')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((plantId: string | null) => {
        const rulePlantId = this.rule?.functionalLocation?.sapWorkCenterId
          ? this.getPlantIdForRule()
          : undefined;
        if (plantId !== rulePlantId) {
          this.clearFormFields([
            'plannerGroup',
            'workCenter',
            'functionalLocation',
          ]);
          this.clearFunctionalLocationOptions();
        }
      });

    this.formGroup
      .get('plannerGroup')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((plannerGroupId: string | null) => {
        const rulePlannerGroupId = this.getPlannerGroupIdForRule();
        if (plannerGroupId !== rulePlannerGroupId) {
          this.clearFormFields(['workCenter', 'functionalLocation']);
          this.clearFunctionalLocationOptions();
        }
      });

    this.formGroup
      .get('workCenter')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((workCenterId: string | null) => {
        this.selectedLabelIds.length = 0;
        this.initialLabelIds.length = 0;
        if (workCenterId) {
          const ruleWorkCenterId =
            this.rule?.functionalLocation?.sapWorkCenterId;
          if (workCenterId !== ruleWorkCenterId) {
            this.clearFormFields(['functionalLocation']);
          }
          this.loadFunctionalLocations(workCenterId);
          this.loadWorkCenterLabels(workCenterId);
        } else {
          this.clearFunctionalLocationOptions();
          this.availableLabels = [];
        }
      });

    // In edit mode, load functional locations for the current work center
    if (
      this.mode === 'edit' &&
      this.rule?.functionalLocation?.sapWorkCenterId
    ) {
      this.loadFunctionalLocations(
        this.rule.functionalLocation.sapWorkCenterId,
      );
    }

    // Load labels for initial workCenter value (edit mode)
    const initialWorkCenter = this.formGroup.get('workCenter')?.value;
    if (initialWorkCenter) {
      this.loadWorkCenterLabels(initialWorkCenter, true);
    }
  }

  onLabelChange(): void {
    this.selectedLabelIdsChange.emit(this.selectedLabelIds);
  }

  private loadFunctionalLocations(workCenterId: string): void {
    this.loadingFunctionalLocations = true;
    this.functionalLocationService
      .GetByWorkCenter(workCenterId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (locations: FunctionalLocation[]) => {
          this.functionalLocationOptions = locations.map((fl) => ({
            label: fl.sapDescription
              ? `${fl.sapId} - ${fl.sapDescription}`
              : fl.sapId,
            value: fl.sapId,
          }));
          this.loadingFunctionalLocations = false;
        },
        error: () => {
          this.functionalLocationOptions = [];
          this.loadingFunctionalLocations = false;
        },
      });
  }

  private loadWorkCenterLabels(
    workCenterId: string,
    initFromRule = false,
  ): void {
    this.loadingLabels = true;
    this.workCenterLabelService
      .getByWorkCenterId(workCenterId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (labels: WorkCenterLabel[]) => {
          this.availableLabels = labels.sort((a, b) =>
            a.label.localeCompare(b.label, 'fr', {
              sensitivity: 'base',
              numeric: true,
            }),
          );
          if (initFromRule && this.rule?.workCenterLabels) {
            const ids = this.rule.workCenterLabels.map((l) => l.uid);
            this.selectedLabelIds.length = 0;
            this.selectedLabelIds.push(...ids);
            this.initialLabelIds.length = 0;
            this.initialLabelIds.push(...ids);
          }
          this.loadingLabels = false;
        },
        error: () => {
          this.availableLabels = [];
          this.loadingLabels = false;
        },
      });
  }

  private getPlantIdForRule(): string | undefined {
    if (!this.rule?.functionalLocation?.sapWorkCenterId) return undefined;
    for (const plant of this.plants) {
      for (const pg of plant.plannerGroups || []) {
        for (const wc of pg.workCenters || []) {
          if (wc.sapId === this.rule.functionalLocation.sapWorkCenterId) {
            return plant.sapId;
          }
        }
      }
    }
    return undefined;
  }

  private getPlannerGroupIdForRule(): string | undefined {
    if (!this.rule?.functionalLocation?.sapWorkCenterId) return undefined;
    for (const plant of this.plants) {
      for (const pg of plant.plannerGroups || []) {
        for (const wc of pg.workCenters || []) {
          if (wc.sapId === this.rule.functionalLocation.sapWorkCenterId) {
            return pg.sapId;
          }
        }
      }
    }
    return undefined;
  }

  private clearFormFields(fields: string[]): void {
    fields.forEach((field) => this.formGroup.get(field)?.setValue(''));
  }

  private clearFunctionalLocationOptions(): void {
    this.functionalLocationOptions = [];
  }

  getFieldError(fieldName: string): string | null {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.invalid || !control.touched) return null;
    if (control.errors?.['required']) return 'Ce champ est requis';
    return 'Valeur invalide';
  }
}
